import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '../../lib/api';
import type { Home } from '@home-guard/shared';

const schema = z.object({
  address_line1: z.string().min(5).max(200).trim(),
  city: z.string().min(2).max(100).trim(),
  state: z.string().length(2).toUpperCase(),
  zip_code: z.string().regex(/^\d{5}(-\d{4})?$/),
  year_built: z.preprocess(v => (v === '' || v == null ? null : Number(v)),
    z.number().int()
      .min(1800, `Please enter a valid year between 1800 and ${new Date().getFullYear()}`)
      .max(new Date().getFullYear(), `Please enter a valid year between 1800 and ${new Date().getFullYear()}`)
      .nullable()),
  sq_footage: z.preprocess(v => (v === '' || v == null ? null : Number(v)),
    z.number().int().min(100).max(100000).nullable()),
  bedrooms: z.preprocess(v => (v === '' || v == null ? null : Number(v)),
    z.number().int().min(0).max(50).nullable()),
  bathrooms: z.preprocess(v => (v === '' || v == null ? null : Number(v)),
    z.number().min(0).max(50).nullable()),
  structure_type: z.enum(['single_family','townhouse','condo','mobile_home','']).optional(),
  roof_type: z.enum(['shingle','metal','tile','flat','unknown','']).optional(),
  foundation_type: z.enum(['slab','crawlspace','basement','unknown','']).optional(),
  fuel_type: z.enum(['natural_gas','propane','electric','oil','mixed','']).optional(),
  has_solar: z.boolean(),
});

type FormData = z.infer<typeof schema>;

const STRUCTURE_OPTS = [
  { value: 'single_family', label: 'Single Family', icon: '🏠' },
  { value: 'townhouse',     label: 'Townhouse',     icon: '🏘' },
  { value: 'condo',         label: 'Condo',         icon: '🏢' },
  { value: 'mobile_home',   label: 'Mobile Home',   icon: '🚐' },
] as const;

const ROOF_OPTS = ['shingle','metal','tile','flat','unknown'] as const;
const FOUNDATION_OPTS = ['slab','crawlspace','basement','unknown'] as const;

const FUEL_OPTS = [
  { value: 'electric',    label: 'Electric',     icon: '⚡' },
  { value: 'natural_gas', label: 'Natural Gas',  icon: '🔥' },
  { value: 'propane',     label: 'Propane',      icon: '🔶' },
  { value: 'oil',         label: 'Oil',          icon: '💧' },
  { value: 'mixed',       label: 'Mixed',        icon: '🔀' },
] as const;

function sectionLabel(text: string) {
  return <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-5 mb-2">{text}</p>;
}

export function EditHome() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const homes = homesData?.data ?? [];
  const home = homes.find(h => h.is_primary) ?? homes[0];

  const {
    register, handleSubmit, reset, watch, setValue,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      address_line1: '', city: '', state: '', zip_code: '',
      year_built: null, sq_footage: null, bedrooms: null, bathrooms: null,
      structure_type: '', roof_type: '', foundation_type: '',
      fuel_type: '', has_solar: false,
    },
  });

  useEffect(() => {
    if (home) {
      reset({
        address_line1: home.address_line1,
        city: home.city,
        state: home.state,
        zip_code: home.zip_code,
        year_built: home.year_built,
        sq_footage: home.sq_footage,
        bedrooms: home.bedrooms,
        bathrooms: home.bathrooms,
        structure_type: home.structure_type ?? '',
        roof_type: home.roof_type ?? '',
        foundation_type: home.foundation_type ?? '',
        fuel_type: home.fuel_type ?? '',
        has_solar: home.has_solar,
      });
    }
  }, [home, reset]);

  const structureVal = watch('structure_type');
  const roofVal = watch('roof_type');
  const foundationVal = watch('foundation_type');
  const fuelVal = watch('fuel_type');
  const hasSolar = watch('has_solar');

  const onSubmit = async (data: FormData) => {
    if (!home) return;
    try {
      await api.put<Home>(`/homes/${home.id}`, {
        address_line1: data.address_line1,
        city: data.city,
        state: data.state,
        zip_code: data.zip_code,
        ...(data.year_built !== null     ? { year_built:     data.year_built }     : {}),
        ...(data.sq_footage !== null     ? { sq_footage:     data.sq_footage }     : {}),
        ...(data.bedrooms !== null       ? { bedrooms:       data.bedrooms }       : {}),
        ...(data.bathrooms !== null      ? { bathrooms:      data.bathrooms }      : {}),
        ...(data.structure_type  ? { structure_type:  data.structure_type  } : {}),
        ...(data.roof_type       ? { roof_type:       data.roof_type       } : {}),
        ...(data.foundation_type ? { foundation_type: data.foundation_type } : {}),
        ...(data.fuel_type       ? { fuel_type:       data.fuel_type       } : {}),
        has_solar: data.has_solar,
      });
      await queryClient.invalidateQueries({ queryKey: ['homes'] });
      navigate('/app/profile');
    } catch (err) {
      setError('root', { message: err instanceof Error ? err.message : 'Save failed.' });
    }
  };

  const input = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-base outline-none focus:border-blue-500 bg-white';
  const chip = (active: boolean) =>
    `px-3 py-2 rounded-xl border text-sm font-medium capitalize cursor-pointer transition-all ${
      active ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 text-gray-700 bg-white'
    }`;

  return (
    <div className="min-h-dvh bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 pt-12 pb-4 flex items-center justify-between">
        <button onClick={() => navigate('/app/profile')} className="flex items-center gap-1 text-gray-600 text-sm font-medium" style={{ minHeight: 44 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Cancel
        </button>
        <h1 className="text-base font-bold text-gray-900">Edit Home</h1>
        <button
          form="edit-home-form"
          type="submit"
          disabled={isSubmitting}
          className="text-blue-600 text-sm font-bold disabled:text-gray-400"
          style={{ minHeight: 44 }}
        >
          {isSubmitting ? 'Saving…' : 'Save'}
        </button>
      </div>

      <form id="edit-home-form" onSubmit={handleSubmit(onSubmit)} className="px-4 pb-12">
        {sectionLabel('Address')}
        <div className="space-y-3">
          <div>
            <input {...register('address_line1')} placeholder="Street address" className={input} />
            {errors.address_line1 && <p className="text-red-500 text-xs mt-1">{errors.address_line1.message}</p>}
          </div>
          <div className="grid grid-cols-[1fr_60px_100px] gap-2">
            <input {...register('city')} placeholder="City" className={input} />
            <input {...register('state')} placeholder="ST" maxLength={2} className={input + ' uppercase'} />
            <input {...register('zip_code')} placeholder="ZIP" className={input} />
          </div>
          {(errors.city ?? errors.state ?? errors.zip_code) && (
            <p className="text-red-500 text-xs">{errors.city?.message ?? errors.state?.message ?? errors.zip_code?.message}</p>
          )}
        </div>

        {sectionLabel('Details')}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Year Built</label>
            <input {...register('year_built')} type="number" placeholder="e.g. 1998" className={input} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Sq Footage</label>
            <input {...register('sq_footage')} type="number" placeholder="e.g. 1800" className={input} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Bedrooms</label>
            <input {...register('bedrooms')} type="number" placeholder="e.g. 3" className={input} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Bathrooms</label>
            <input {...register('bathrooms')} type="number" step="0.5" placeholder="e.g. 2" className={input} />
          </div>
        </div>

        {sectionLabel('Structure Type')}
        <div className="grid grid-cols-2 gap-3">
          {STRUCTURE_OPTS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setValue('structure_type', structureVal === opt.value ? '' : opt.value)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                structureVal === opt.value
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-gray-200 text-gray-700 bg-white'
              }`}
              style={{ minHeight: 52 }}
            >
              <span>{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>

        {sectionLabel('Roof Type')}
        <div className="flex flex-wrap gap-2">
          {ROOF_OPTS.map(v => (
            <button key={v} type="button"
              onClick={() => setValue('roof_type', roofVal === v ? '' : v)}
              className={chip(roofVal === v)}
              style={{ minHeight: 44 }}
            >
              {v.replace(/_/g, ' ')}
            </button>
          ))}
        </div>

        {sectionLabel('Foundation Type')}
        <div className="flex flex-wrap gap-2">
          {FOUNDATION_OPTS.map(v => (
            <button key={v} type="button"
              onClick={() => setValue('foundation_type', foundationVal === v ? '' : v)}
              className={chip(foundationVal === v)}
              style={{ minHeight: 44 }}
            >
              {v.replace(/_/g, ' ')}
            </button>
          ))}
        </div>

        {sectionLabel('Fuel Type')}
        <div className="grid grid-cols-3 gap-2">
          {FUEL_OPTS.map(opt => (
            <button key={opt.value} type="button"
              onClick={() => setValue('fuel_type', fuelVal === opt.value ? '' : opt.value)}
              className={`flex flex-col items-center gap-1 py-3 rounded-xl border text-xs font-medium transition-all ${
                fuelVal === opt.value
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-gray-200 text-gray-700 bg-white'
              }`}
              style={{ minHeight: 64 }}
            >
              <span className="text-xl">{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>

        {sectionLabel('Solar Panels')}
        <div className="flex gap-3">
          {([true, false] as const).map(val => (
            <button key={String(val)} type="button"
              onClick={() => setValue('has_solar', val)}
              className={`flex-1 py-3 rounded-xl border text-sm font-semibold transition-all ${
                hasSolar === val
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-gray-200 text-gray-700 bg-white'
              }`}
              style={{ minHeight: 48 }}
            >
              {val ? 'Yes — I have solar' : 'No solar'}
            </button>
          ))}
        </div>

        {errors.root && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm">
            {errors.root.message}
          </div>
        )}
      </form>
    </div>
  );
}
